import { PERSON_GENDERS, SHORTLIST_ATHLETE_ROLE_FILTERS, SHORTLIST_ORG_STATES } from "@seagames/contracts";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { PageSkeleton } from "../../components/accommodation/page-skeleton";
import { SPORT_SORTS } from "../../components/shortlist/model";
import { type ShortlistSportsSearch, ShortlistSportsPage } from "../../components/shortlist/sports-page";
import { useDefaultGamesId } from "../../lib/competition/games";
import { searchText } from "../../lib/list-page/list-search";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

/**
 * Shortlist (M7a): the NOC overview, then one organisation's sports and
 * athletes. URL = the page's state, every param lenient. Who sees what is
 * the API's call (`cardInScope`); the page only picks the organisation.
 */
export const Route = createFileRoute("/_app/accreditation/shortlist/")({
	validateSearch: z.object({
		games_id: lenient(z.string().uuid()),
		org: lenient(z.string().uuid()),
		// The NOC overview.
		search: lenient(searchText()),
		progress: lenient(z.enum(SHORTLIST_ORG_STATES)),
		page: lenient(z.coerce.number().int().min(1)),
		// One organisation: the sports cards.
		tab: lenient(z.literal("athletes")),
		q: lenient(searchText()),
		sort: lenient(z.enum(SPORT_SORTS)),
		// One organisation: every athlete.
		athlete_q: lenient(searchText()),
		sport: lenient(z.string().uuid()),
		gender: lenient(z.enum(PERSON_GENDERS)),
		role: lenient(z.enum(SHORTLIST_ATHLETE_ROLE_FILTERS)),
	}),
	staticData: { contentTop: "tabbed" },
	component: ShortlistPage,
});

function ShortlistPage() {
	const search = Route.useSearch() as ShortlistSportsSearch;
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	if (!gamesId) return <PageSkeleton />;
	return <ShortlistSportsPage gamesId={gamesId} search={search} patch={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })} />;
}
